import React from 'react';
import { Check } from 'lucide-react';
import { usePricing } from '../contexts/PricingContext';
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan';
import { buildInclusions, monthlyInclusions, salesSchoolInclusion, commitments } from '../data/advantagePricing';
const usd = (value: number) => '$' + value.toLocaleString('en-US');
type SectionProps = {
  index: number;
  label: string;
  caption: string;
  children: React.ReactNode;
};
function Section({
  index,
  label,
  caption,
  children
}: SectionProps) {
  return <section className="grid grid-cols-1 md:grid-cols-[7rem_1fr] gap-3 md:gap-6 py-7 border-t border-slate-800" data-id="element-746" data-mp="1fni5mr5hexjv">
      <div className="md:pt-1" data-id="element-747" data-mp="1kgjm33lc755k">
        <div className="flex items-baseline gap-1.5" data-id="element-748" data-mp="1pel7hv68jgk7">
          <span className="text-[9px] font-bold tabular-nums text-[#7FE0C3]/60" data-id="element-749" data-mp="1pecve5hxsfa2">
            {String(index).padStart(2, '0')}
          </span>
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-[#7FE0C3]" data-id="element-750" data-mp="1zcsm47d1xmj1">
            {label}
          </h2>
        </div>
        <p className="mt-1 text-[11px] text-slate-500 leading-snug" data-id="element-751" data-mp="3o9tvrtkeyl8">
          {caption}
        </p>
      </div>
      <div data-id="element-752" data-mp="3us6wd54os509">{children}</div>
    </section>;
}
export function AdvantageSummary() {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    splitBuildFee,
    setHasRecruiter,
    leaderBuildFee,
    leaderMonthlyFee,
    leaderActiveFee,
    leaderAdSpendLow,
    buildFee,
    scopeMultiplier,
    platformFee,
    campaignFee,
    recruiterFee,
    monthlyTotal,
    hiresLow,
    hiresHigh
  } = usePricing();
  const plan = getBuildPaymentPlan(buildFee);
  const buildAtSigning = splitBuildFee ? plan.down : buildFee;
  const buildInstallment = splitBuildFee ? plan.monthly : 0;
  const dailyAdSpend = Math.round(adSpend / 30);
  // The leader retention bonus is performance based, so it rides in the
  // "+ bonuses" language rather than the monthly fee total.
  const monthlyWithLeader = monthlyTotal;
  // With a leadership campaign, only the implementation and the first month of
  // the leadership campaign are collected up front.
  const dueAtSigning = hasLeader ? buildAtSigning + leaderBuildFee + leaderMonthlyFee : buildAtSigning + monthlyTotal;
  const phases = [{
    window: 'First 9–30 days',
    title: 'Implementation and Alignment',
    fees: `${usd(dueAtSigning)} at signing`,
    ad: 'Not running yet',
    expect: ['System, funnel, and creative built', 'Full alignment with your team', '3–4 hours of training and alignment calls']
  }, ...(hasLeader ? [{
    window: '2–8 weeks',
    title: 'Leadership Campaign',
    fees: `${usd(leaderMonthlyFee)} / mo`,
    ad: `${usd(leaderAdSpendLow)} / mo`,
    expect: ['Targeted outreach to experienced operators', 'Interviews run and vetted by HIRE', 'Standard campaign launches once the leader signs']
  }] : []), {
    window: 'Month 1',
    title: 'Launch and Refinement',
    fees: hasLeader ? `${usd(monthlyTotal)} / mo` : 'Covered at signing',
    ad: `${usd(adSpend)} / mo`,
    expect: [`Your first ${hiresLow}–${hiresHigh} hires`, 'Weekly support and consulting', 'Baseline KPIs established', '30-day guarantee starts at launch']
  }, {
    window: 'Months 2–3',
    title: 'Optimization and Scale',
    fees: `${usd(monthlyWithLeader + buildInstallment)} / mo + bonuses`,
    ad: `${usd(adSpend)} / mo`,
    expect: ['Conversion rates climb', 'Retention improves and ROI shows up', 'Most partners increase investment here']
  }, {
    window: 'Month 4 onward',
    title: 'Predictable Engine',
    fees: `${usd(monthlyWithLeader)} / mo + bonuses`,
    ad: `${usd(adSpend)} / mo`,
    expect: ['Forecastable hiring output', 'Scope moves with your ad spend', 'Continuous optimization']
  }];
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 font-sans px-5 md:px-10 py-14 md:py-16" data-id="element-753" data-mp="pdp0xdpzq0cq">
      <div className="max-w-5xl mx-auto" data-id="element-754" data-mp="uemgdijm8gaj">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4 pb-6" data-id="element-755" data-mp="zce7iacfrydo">
          <div data-id="element-756" data-mp="zabl00ahsndp">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-1" data-id="element-757" data-mp="1p5rguv7fhj2">
              HIRE Advantage
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" data-id="element-758" data-mp="6os698qol5tz">
              Your Numbers at a Glance
            </h1>
          </div>
          <button type="button" onClick={() => setHasRecruiter(!hasRecruiter)} aria-pressed={hasRecruiter} className={`no-print px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${hasRecruiter ? 'bg-amber-400/10 text-amber-400 border border-amber-400/40' : 'text-slate-500 border border-slate-800 hover:text-slate-200'}`} data-id="element-759" data-mp="gdtjir5ud949">
            {hasRecruiter ? 'Recruiter added' : 'Add a recruiter'}
          </button>
        </header>

        {/* 01 — Projection */}
        <Section index={1} label="Projection" caption="What your ad spend is expected to produce." data-id="element-760" data-mp="1n6y5bihrin14">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 rounded-xl overflow-hidden" data-id="element-761" data-mp="1ib0ufrp9q8vq">
            <div className="bg-[#0A0F1C] px-6 py-6" data-id="element-762" data-mp="19g314ji7ku3v">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-763" data-mp="19gulsza9p7fd">
                Recommended Monthly Ad Spend
              </div>
              <div className="text-4xl font-bold text-white tracking-tight" data-id="element-764" data-mp="zhdgwk2uaxkg">
                {usd(adSpend)}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-765" data-mp="uk3vrgmhh82f">
                ({usd(dailyAdSpend)} / day)
              </div>
              <p className="mt-3 text-xs font-bold text-[#7FE0C3] leading-snug" data-id="element-766" data-mp="ky2mvzcjt9pq">
                Paid directly to the job platforms. Separate from HIRE's fees.
              </p>
            </div>
            <div className="bg-[#0A0F1C] px-6 py-6" data-id="element-767" data-mp="bxe832fbz5bs">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-768" data-mp="fxoh6nyf6buv">
                Expected Monthly Output
              </div>
              <div className="text-4xl font-bold text-[#7FE0C3] tracking-tight" data-id="element-769" data-mp="18lapg9ile4o2">
                {hiresLow}–{hiresHigh}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-770" data-mp="ut4csxeneftd">
                hires / month at {usd(cpaLow)}–{usd(cpaHigh)} per hire
              </div>
            </div>
          </div>
        </Section>

        {/* 02 — Investment */}
        <Section index={2} label="Investment" caption="The three components of what HIRE charges." data-id="element-771" data-mp="1miz3axe8sbjm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-772" data-mp="3c1p61zam45bq">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col" data-id="element-773" data-mp="3bzul4kzccd7n">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-774" data-mp="3719v1edsz9wx">
                Implementation
              </div>
              <div className="text-2xl font-bold text-white" data-id="element-775" data-mp="3gtyufaw2wxbw">
                {usd(buildFee)}
                <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-776" data-mp="2t4eaaoo6vu11">
                  one time
                </span>
              </div>
              {splitBuildFee && <p className="mt-1 text-xs text-slate-500" data-id="element-777" data-mp="33225ffppj3jq">
                  Split: {usd(plan.down)} at signing, then {usd(plan.monthly)}{' '}
                  in months 2 and 3.
                </p>}
              <ul className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-778" data-mp="ia8pstmeiyuz">
                {buildInclusions.map(point => <li key={point} className="flex items-start" data-id="element-779" data-mp="dbxfkhs72mc8">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-780" data-mp="26vpd9obg860i" />
                    <span className="text-slate-400 text-xs leading-snug" data-id="element-781" data-mp="2bthexa1wb6hw">
                      {point}
                    </span>
                  </li>)}
                {hasRecruiter && <li className="flex items-start" data-id="element-782" data-mp="2lk3lyfvjqooz">
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-783" data-mp="2lln5cfci1fuz" />
                    <span className="text-amber-400 text-xs leading-snug" data-id="element-784" data-mp="2qkzr0wrmwgvz">
                      Corporate recruiter aligned to your ICP and hiring
                      standards
                    </span>
                  </li>}
              </ul>
            </div>

            <div className="rounded-xl border border-[#7FE0C3]/40 bg-slate-900/40 p-5 flex flex-col relative" data-id="element-785" data-mp="34i688mwpm7j3">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-786" data-mp="157esxdlg0mx2" />
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-787" data-mp="1a4x5rf2kzmac">
                Monthly Service
              </div>
              <div className="text-2xl font-bold text-white" data-id="element-788" data-mp="xtw496v9t9t1">
                {usd(monthlyTotal)}
                <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-789" data-mp="sut7satcwbwo">
                  / month
                </span>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-790" data-mp="nxt3o5g2hj87">
                <div className="flex justify-between text-xs" data-id="element-791" data-mp="1liyuv47vuczo">
                  <span className="text-slate-400" data-id="element-792" data-mp="1gl6s1f5p96gp">Partnership</span>
                  <span className="text-white" data-id="element-793" data-mp="1giub95boh258">{usd(platformFee)}</span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-794" data-mp="1bjzlfjfamdbm">
                  <span className="text-slate-400" data-id="element-795" data-mp="16lpnj126oymb">
                    Pipeline management{' '}
                    <span className="text-slate-500" data-id="element-796" data-mp="7qd5trswlbp">({scopeMultiplier}x)</span>
                  </span>
                  <span className="text-white" data-id="element-797" data-mp="3rbx4usg57uvd">{usd(campaignFee)}</span>
                </div>
                {hasRecruiter && <div className="flex justify-between text-xs" data-id="element-798" data-mp="34q38ffp7vl52">
                    <span className="text-amber-400" data-id="element-799" data-mp="2zrs0jaihfkph">
                      Corporate recruiter{' '}
                      <span className="opacity-70" data-id="element-800" data-mp="3egoetpb4ttyj">({scopeMultiplier}x)</span>
                    </span>
                    <span className="text-amber-400" data-id="element-801" data-mp="39o4r3ziqdig0">{usd(recruiterFee)}</span>
                  </div>}
              </div>
              <ul className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-802" data-mp="3ni5u55gptrm6">
                {monthlyInclusions.map(point => <li key={point} className="flex items-start" data-id="element-803" data-mp="19mqujz1776y">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-804" data-mp="170g6bgugbki" />
                    <span className="text-slate-400 text-xs leading-snug" data-id="element-805" data-mp="3sg7f5vxpc10a">
                      {point}
                    </span>
                  </li>)}
                <li className="flex items-start" data-id="element-806" data-mp="1y2oj6qjbkqh3">
                  <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-807" data-mp="djcbprrsshfq" />
                  <span className="text-white text-xs font-bold leading-snug" data-id="element-808" data-mp="dlota3tn8ps9">
                    {salesSchoolInclusion}
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col" data-id="element-809" data-mp="3ropq6e0fsln">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-810" data-mp="8q9i5jn0t7zj">
                Performance-Based Pricing
              </div>
              <div className="space-y-3" data-id="element-811" data-mp="3vywpuh5zko13">
                <div data-id="element-812" data-mp="3h02p4rpplwrg">
                  <div className="text-xs text-white font-medium" data-id="element-813" data-mp="3h0kvwi9zztdm">
                    Placement bonus
                  </div>
                  <div className="text-2xl font-bold text-white" data-id="element-814" data-mp="3lzxf8t1nuibg">
                    {usd(500)}
                    <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-815" data-mp="1f9zk8xe5n74o">
                      per hire
                    </span>
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-816" data-mp="1iqca8xpar5oq">
                    Day 15 active in the field
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-800" data-id="element-817" data-mp="1dpy11cbylpp4">
                  <div className="text-xs text-white font-medium" data-id="element-818" data-mp="18ro3ww6ocf0v">
                    Retention bonuses
                  </div>
                  <div className="text-2xl font-bold text-white" data-id="element-819" data-mp="14sfaxtjgkl6d">
                    {usd(1000)}
                    <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-820" data-mp="21gllex32a7sk">
                      per hire
                    </span>
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-821" data-mp="1rqs6pn7mhck3">
                    Day 45 and day 90 active in the field
                  </div>
                </div>
              </div>
              <p className="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-400 leading-snug" data-id="element-822" data-mp="2k7l63m3iwvg1">
                Billed only when a hire is still active in the field. You pay
                for the reps you retain, instead of resumés or hires.
              </p>
            </div>
          </div>
        </Section>

        {/* 03 — Timeline */}
        <Section index={3} label="Timeline" caption="What happens when, and what is owed in each window." data-id="element-823" data-mp="6y4jsn1b5nkf">
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 rounded-xl overflow-hidden ${hasLeader ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-id="element-824" data-mp="fy00f7dmuaru">
            {phases.map(phase => <div key={phase.window} className="bg-[#0A0F1C] px-5 py-5 flex flex-col" data-id="element-825" data-mp="puwa3gf9p2xz">
                <div className="text-sm font-bold text-[#7FE0C3]" data-id="element-826" data-mp="up9xownou5s3">
                  {phase.window}
                </div>
                <div className="text-[11px] text-slate-500" data-id="element-827" data-mp="13nu9reyqth7c">{phase.title}</div>

                <ul className="mt-3.5 pb-5 space-y-2" data-id="element-828" data-mp="13lrcc9v9glsl">
                  {phase.expect.map(item => <li key={item} className="flex items-start" data-id="element-829" data-mp="uzyfjrim5ck4">
                      <span className="w-1 h-1 rounded-full bg-[#7FE0C3]/60 mr-2.5 mt-1.5 shrink-0" data-id="element-830" data-mp="uydhvt105di1" />
                      <span className="text-slate-300 text-[13px] leading-snug" data-id="element-831" data-mp="q0lheamakj28">
                        {item}
                      </span>
                    </li>)}
                </ul>

                <dl className="mt-auto pt-5 border-t border-slate-800 space-y-1.5 text-[11px]" data-id="element-832" data-mp="zrge68x5z11x">
                  <div className="flex justify-between gap-3" data-id="element-833" data-mp="c1cda9bd1fem">
                    <dt className="text-slate-500" data-id="element-834" data-mp="732ihztjsgtj">HIRE</dt>
                    <dd className="text-slate-300 text-right" data-id="element-835" data-mp="lza0cthhh65n">{phase.fees}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-836" data-mp="h06whcd8p5z9">
                    <dt className="text-slate-500" data-id="element-837" data-mp="gyvr5845bq61">Ads</dt>
                    <dd className="text-slate-300 text-right" data-id="element-838" data-mp="1xdnl3pgxx5hg">{phase.ad}</dd>
                  </div>
                </dl>
              </div>)}
          </div>
        </Section>

        {/* 04 — Terms */}
        <Section index={4} label="Terms" caption="What we guarantee, and what we need from you." data-id="element-839" data-mp="1zxtmpqi30juu">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-840" data-mp="24vv5eablih5s">
            <div className="rounded-xl border border-[#7FE0C3]/40 bg-[#7FE0C3]/5 p-5" data-id="element-841" data-mp="1qzi0s1s5ua8q">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-842" data-mp="1v0thtiwyxn5b">
                Our Guarantee
              </div>
              <p className="text-lg font-bold text-white leading-snug" data-id="element-843" data-mp="2irp4y2qhbu68">
                100% of our fees back inside the first 30 days of the campaign.
              </p>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed" data-id="element-844" data-mp="29wj7br1d4ies">
                We take the bigger risk on this partnership than you do.
              </p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-id="element-845" data-mp="31n5smzmqc7u9">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-3" data-id="element-846" data-mp="36f710ags1ef0">
                What We Need From You
              </div>
              <ul className="space-y-1.5" data-id="element-847" data-mp="3g8l28zka8j53">
                {commitments.map((commitment, index) => <li key={commitment.title} className="flex items-start text-xs text-slate-400" data-id="element-848" data-mp="3c7hoqjppiz71" data-mpi={index}>
                    <span className="text-[#7FE0C3] font-bold mr-2" data-id="element-849" data-mp="32acyo8sqqjky">
                      {index + 1}.
                    </span>
                    <span data-id="element-850" data-mp="2xe6236m8ikfo">
                      <span className="text-white font-medium" data-id="element-851" data-mp="2sll9u9iz8fe9">
                        {commitment.title}.
                      </span>{' '}
                      {commitment.description}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </Section>
      </div>
    </div>;
}